import React, { useState } from 'react'
import classNames from 'classnames'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { ButtonVariant } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { GuildCopperHiFiIcon } from '@/atoms/Icons/GuildCopperHiFiIcon'
import { GuildSilverIcon } from '@/atoms/Icons/GuildSilverIcon'
import { PlusIcon } from '@/atoms/Icons/PlusIcon'
import { CaptionLG, CaptionMD, CaptionSM, CaptionXS, LabelSM } from '@/atoms/Text'
import { GuildOffer } from '@/constants/guild'
import { GuildSignupButton } from '@/molecules/GuildSignupButton'
import { useGuildMember } from '@/services/GuildUserService'
import { formatPrice } from '@/services/PriceFormatter'
import { formatPromotionPriceDisplay, ValidatedPromotion } from '@/services/SaleService'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { OmniGuildPricingLabel } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel'
import { OmniPricePerInterval } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel/OmniGuildPricingLabel'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

export interface GuildPlanBoxProps {
  className?: string
  offers: GuildOffer[]
  orderClass?: string
  disclosePriceInUSD?: boolean
  isGuildIconBelowPriceWells?: boolean
}

export const OmniGuildPlanBox: React.FC<GuildPlanBoxProps> = ({
  className,
  offers,
  disclosePriceInUSD = false,
  isGuildIconBelowPriceWells = false,
}) => {
  const { t } = useTranslate('guild')
  const { isGuildMember } = useGuildMember()
  const { sale, experiments } = useGuildSignupContext()
  type OfferTier = 'basic' | 'basic-no-ads' | 'premium'
  const [selectedOffer, setSelectedOffer] = useState<OfferTier>('premium')
  const offer = offers.find((o) => o.tier === selectedOffer) || offers[0]
  const yearlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.annual.bimOfferId)
  const monthlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.monthly.bimOfferId)
  const selectedClasses = 'bg-black text-white border-black'
  const iconBelowPriceWells = Boolean(experiments['guild-icon-below-price-wells'] || isGuildIconBelowPriceWells)

  const SELECTED_BG_CLASS_MAP = {
    basic: selectedClasses,
    'basic-no-ads': 'border-none bg-[linear-gradient(131deg,#000_12.02%,#000_67.58%,#7C5230_93.92%)]',
    premium: 'border-none bg-[linear-gradient(129deg,#000_11.61%,#845A29_95.18%)]',
  }

  const orderedOffers = offers.sort((a, b) => a.monthly.standardPrice - b.monthly.standardPrice)

  const { locale } = useLocale()

  if (!offer) {
    return null
  }

  const getMonthlyPrice = (offer: GuildOffer) => {
    const offerId = offer.monthly.bimOfferId
    const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
    const isPromoApplicable = Boolean(promotion)

    if (isPromoApplicable) {
      return formatPromotionPriceDisplay(promotion, locale)
    }
    return formatPrice(offer.monthly.standardPrice, { locale, includeDecimals: false, currency: 'USD' })
  }

  return (
    <div className={classNames(className, 'flex justify-center items-center w-full px-2 md:px-4')}>
      <div
        className={classNames(
          'relative h-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-white p-2 text-left text-core-gray-950 md:flex md:p-4',
          iconBelowPriceWells ? 'min-h-[655px] md:min-h-[656px] lg:min-h-[672px]' : 'min-h-[714px]',
        )}
      >
        <CaptionXS weight="bold">Choose your plan</CaptionXS>
        <div className="relative mt-2 flex w-full items-center justify-between gap-2">
          <div className="grid w-full grid-cols-3 gap-1">
            {orderedOffers.map((off, id) => (
              <div
                key={`${off.tier}-${id}`}
                className={classNames('relative col-span-1 rounded-lg h-[120px]', {
                  'p-[2px]': off.tier === 'premium',
                })}
                style={{
                  background:
                    'linear-gradient(159deg, #EDD7C6 0%, #D6A177 13.88%, #FEDEC6 40.62%, #D48D59 62.21%, #A66141 88.77%, #612B12 98.15%)',
                }}
              >
                <When condition={off.tier === 'premium'}>
                  <span className="absolute left-[50%] top-[-20px] ml-[-39px] h-5 w-[78px] rounded-t-md bg-[#DFAF89] pt-1 text-center text-[10px] font-bold">
                    Best Value
                  </span>
                </When>
                <button
                  className={classNames(
                    'flex flex-col items-start justify-between rounded-lg border p-2 md:p-4 md:pt-2 text-start w-full h-full',
                    off.tier === selectedOffer
                      ? SELECTED_BG_CLASS_MAP[off.tier]
                      : 'border-core-gray-300 bg-core-gray-100',
                  )}
                  onClick={() => setSelectedOffer(off.tier)}
                >
                  <Switch>
                    <Case condition={off.tier === 'basic'}>
                      <div>
                        <CaptionSM
                          className="md:photon-paragraph-md"
                          color={off.tier === selectedOffer ? 'white' : 'black'}
                          weight="bold"
                        >
                          {t('basicWithAds', 'Basic with Ads')}
                        </CaptionSM>
                        <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                          {t('streamIn1080p', 'Stream in 1080p')}
                        </CaptionXS>
                      </div>
                      <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'basic-no-ads'}>
                      <div>
                        <CaptionSM
                          className="md:photon-paragraph-md"
                          color={off.tier === selectedOffer ? 'white' : 'black'}
                          weight="bold"
                        >
                          {t('basic', 'Basic')}
                        </CaptionSM>
                        <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                          {t('streamIn1080p', 'Stream in 1080p')}
                        </CaptionXS>
                      </div>
                      <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'premium'}>
                      <div>
                        <CaptionSM
                          className="md:photon-paragraph-md"
                          color={off.tier === selectedOffer ? 'white' : 'black'}
                          weight="bold"
                        >
                          {t('premium', 'Premium')}
                        </CaptionSM>
                        <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                          {t('streamIn4KFreeMovieTicketsAndMore', 'Stream in 4K, Free Movie Tickets & More')}
                        </CaptionXS>
                      </div>
                      <CaptionXS color={off.tier === selectedOffer ? 'core-gray-200' : 'core-gray-800'}>
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                  </Switch>
                  <When condition={off.tier === selectedOffer}>
                    <CircleCheckIcon
                      size={16}
                      color1="white"
                      color2="black"
                      className="absolute right-4 top-2 md:top-4"
                    />
                  </When>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 pb-4 pt-6">
          <ul className={classNames('flex flex-col gap-2')}>
            <When condition={offer.tier === 'basic'}>
              <When condition={!iconBelowPriceWells}>
                <li className="mb-2 flex items-center justify-center gap-2">
                  <GuildSilverIcon size={31} className="w-full max-w-[31px]" />
                  <CaptionSM>
                    <Translate i18nKey="supportAngelAndOurShowsWithLimitedAdsV2" t={t}>
                      Stream our library with limited ads and help us continue to release more shows you&apos;ll love.
                    </Translate>
                  </CaptionSM>
                </li>
                <li className="relative mb-4 mt-2">
                  <hr className="border-gray-300" />
                </li>
              </When>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="limitedAds">
                    Limited Ads
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'basic-no-ads'}>
              <When condition={!iconBelowPriceWells}>
                <li className="mb-2 flex items-center justify-center gap-2">
                  <GuildSilverIcon size={31} className="w-full max-w-[31px]" />
                  <CaptionSM>
                    <Translate i18nKey="supportAngelAndOurShowsAdFreeNoTicketsV2" t={t}>
                      Don&apos;t like ads? Stream Ad-Free and support us as we continue to release more shows
                      you&apos;ll love.
                    </Translate>
                  </CaptionSM>
                </li>
                <li className="relative mb-4 mt-2">
                  <hr className="border-gray-300" />
                </li>
              </When>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'premium'}>
              <When condition={!iconBelowPriceWells}>
                <li className="mb-2 flex items-center justify-center gap-2">
                  <GuildCopperHiFiIcon size={31} className="w-full max-w-[31px]" />
                  <CaptionSM>
                    <Translate i18nKey="supportAngelAndOurShowsAdFreeV2" t={t}>
                      Stream Ad-Free, see every Angel movie in theaters and make the biggest impact on the future of
                      entertainment.
                    </Translate>
                  </CaptionSM>
                </li>
                <li className="relative mb-4 mt-2">
                  <hr className="border-gray-300" />
                  <LabelSM className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                    PLUS
                  </LabelSM>
                </li>
              </When>
              <li className="flex items-center gap-2">
                <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                <CaptionMD>
                  <Translate t={t} i18nKey="everythingFromBasic">
                    Everything you get from Basic
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <PlusIcon size={16} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <PlusIcon size={16} color="black" />
                <CaptionMD>
                  <Translate t={t} i18nKey="streamIn4kUltraHd">
                    Stream in 4K (Ultra HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <PlusIcon size={16} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="twoTicketsToEveryReleasev4">
                    Two tickets to every Angel movie in theaters to use yourself or give away
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-center gap-2">
                <div className="min-w-4 shrink-0 grow-0">
                  <PlusIcon size={16} color="black" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="discountOnMerchV3">
                    Save 20% on merch in our online store
                  </Translate>
                </CaptionMD>
              </li>
            </When>
          </ul>
        </div>
        <div className="mt-auto flex flex-col gap-2 pb-5 pt-4">
          <PlanSelectorWell
            className="order-1"
            offer={offer}
            currency="USD"
            promotion={monthlyPromotion}
            planInterval="monthly"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
          />
          <PlanSelectorWell
            className="order-0"
            currency="USD"
            offer={offer}
            promotion={yearlyPromotion}
            planInterval="annual"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
          />
        </div>
        <When condition={iconBelowPriceWells}>
          <div className="mb-2">
            <When condition={offer.tier === 'basic'}>
              <div className="flex items-center gap-2">
                <GuildSilverIcon size={31} className="w-full max-w-[31px]" />
                <CaptionSM>
                  <Translate i18nKey="supportFilmsFilmmakersShareValues" t={t}>
                    Support films & filmmakers that share your values.
                  </Translate>
                </CaptionSM>
              </div>
            </When>
            <When condition={offer.tier === 'basic-no-ads'}>
              <div className="flex items-center gap-2">
                <GuildSilverIcon size={31} className="w-full max-w-[31px]" />
                <CaptionSM>
                  <Translate i18nKey="supportFilmsFilmmakersShareValues" t={t}>
                    Support films & filmmakers that share your values.
                  </Translate>
                </CaptionSM>
              </div>
            </When>
            <When condition={offer.tier === 'premium'}>
              <div className="flex items-center gap-2">
                <GuildCopperHiFiIcon size={31} className="w-full max-w-[31px]" />
                <CaptionSM>
                  <Translate i18nKey="supportFilmsFilmmakersShareValues" t={t}>
                    Support films & filmmakers that share your values.
                  </Translate>
                </CaptionSM>
              </div>
            </When>
          </div>
        </When>
      </div>
    </div>
  )
}

interface PlanSelectorWellProps {
  currency: string
  promotion?: ValidatedPromotion
  isGuildMember?: boolean
  planInterval: 'monthly' | 'annual'
  punchyButtonColors?: boolean
  disclosePriceInUSD?: boolean
  className?: string
  offer: GuildOffer
}

function useButtonStyles({
  planInterval,
}: {
  punchyButtonColors?: boolean
  tier?: 'premium' | 'basic' | 'basic-no-ads'
  planInterval: 'monthly' | 'annual'
}) {
  const buttonClassName = ''

  const buttonVariant: ButtonVariant = planInterval === 'annual' ? 'black' : 'plain'

  return { buttonClassName, buttonVariant }
}

export const PlanSelectorWell: React.FC<PlanSelectorWellProps> = ({
  currency,
  promotion,
  planInterval,
  isGuildMember,
  punchyButtonColors,
  disclosePriceInUSD,
  className,
  offer,
}) => {
  const { t } = useTranslate('guild')

  const { buttonClassName, buttonVariant } = useButtonStyles({ punchyButtonColors, tier: offer.tier, planInterval })

  return (
    <div className={classNames('flex rounded-lg bg-core-gray-100 p-4', className)}>
      <div>
        <If condition={planInterval === 'annual'}>
          <Then>
            <CaptionLG weight="semibold" className="uppercase">
              <Translate i18nKey="annual" t={t}>
                Annual
              </Translate>
            </CaptionLG>
          </Then>
          <Else>
            <CaptionLG weight="semibold" className="uppercase">
              <Translate i18nKey="monthly" t={t}>
                Monthly
              </Translate>
            </CaptionLG>
          </Else>
        </If>
        <div className="-mb-3">
          <OmniGuildPricingLabel currency={currency} planInterval={planInterval} offer={offer} />
        </div>
      </div>
      <div className="ml-auto">
        <OmniPricePerInterval
          className="mb-2"
          planInterval={planInterval}
          disclosePriceInUSD={disclosePriceInUSD}
          offer={offer}
        />

        <GuildSignupButton
          currency={currency}
          offerId={offer[planInterval].bimOfferId}
          price={offer[planInterval].standardPrice}
          promoCode={promotion ? promotion?.promotionCode.code : null}
          planInterval={planInterval}
          className={classNames('ml-auto !w-fit', buttonClassName)}
          buttonVariant={buttonVariant}
        >
          <LabelSM className="capitalize">
            <If condition={isGuildMember}>
              <Then>
                <Translate t={t} i18nKey="upgradeMembership">
                  Upgrade Membership
                </Translate>
              </Then>
              <Else>
                <Translate t={t} i18nKey="select">
                  Select
                </Translate>
              </Else>
            </If>
          </LabelSM>
        </GuildSignupButton>
      </div>
    </div>
  )
}
